import { recipe } from "@vanilla-extract/recipes";
import { rem } from "@huub-web/utils/css/rem";
import { style } from "@vanilla-extract/css";

import { theme } from "../../theme/css/theme.css";
import { transition } from "../../utils/transition";
import { disabledState } from "../../theme/disabledState";
import { responsiveContainer } from "../../utils/responsive";

export const tabsNavigationStyle = style({
  // Don't remove this style, it's used as parent selector
});

export const containerStyle = recipe({
  base: {
    position: "relative",
    width: "100%",
  },
  variants: {
    variant: {
      default: {
        gap: theme.spacings.s,
      },
      docs: {},
      outlined: {},
    },
    color: {
      default: {},
      menu: {},
      background: {},
    },
  },
  defaultVariants: {
    variant: "default",
    color: "default",
  },
});

export const navigationStyle = recipe({
  base: {
    position: "relative",
    display: "flex",
    alignItems: "flex-end",
    listStyle: "none",
    padding: 0,
    margin: 0,
    gap: theme.spacings.xs,
    width: "100%",
    ":focus-visible": {
      zIndex: 2,
    },
  },
  variants: {
    variant: {
      default: {
        borderRadius: theme.borderRadiuses.m,
      },
      docs: {
        borderRadius: theme.borderRadiuses.xxs,
      },
      outlined: {
        justifyContent: "flex-start",
        borderRadius: rem(1),
        height: rem(44),
        paddingLeft: theme.spacings.xs,
      },
    },
    indented: {
      true: {
        padding: `0 ${theme.spacings.m}`,
      },
    },
  },
});

export const tabItemStyle = recipe({
  base: {
    transition: transition(
      "color",
      "outline",
      "background-color",
      "border-color",
      "opacity",
      {
        property: "transform",
        duration: theme.transition.duration.xs,
      },
    ),
    selectors: {
      "&:not([disabled]):not([data-active])": {
        cursor: "pointer",
      },
    },
    lineHeight: 1,
  },
  variants: {
    disabled: {
      true: {
        ...disabledState,
      },
    },
    variant: {
      default: {
        background: "var(--background)",
        color: "var(--color)",
        borderRadius: theme.borderRadiuses.m,
        padding: `${theme.spacings.s} ${theme.spacings.m}`,
      },
      docs: {
        padding: `${theme.spacings.s} ${theme.spacings.m}`,
        borderRadius: theme.borderRadiuses.s,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        color: "var(--color)",
        selectors: {
          "&[data-active]": {
            background: "var(--background)",
            borderBottomColor: "var(--background)",
          },
        },
      },
      outlined: {
        borderRadius: theme.borderRadiuses.xs,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        marginBottom: "-1px",
        background: "transparent",
        border: `1px solid ${theme.colors.defaultBorder}`,
        height: rem(42),
        padding: `0 ${theme.spacings.s}`,
        transition: transition("height"),
        selectors: {
          "&[data-active]": {
            borderBottomWidth: 0,
            background: "var(--background)",
            fontWeight: "bold",
            height: rem(44),
          },
          "&:not([data-active])": {
            height: rem(42),
            background: theme.colors.backgroundTintMarketing,
          },
          "&:not([data-active]):hover": {
            height: rem(44),
          },
        },
      },
    },
    color: {
      default: {
        vars: {
          "--background": theme.colors.secondary,
          "--color": theme.colors.onSecondary,
        },
        selectors: {
          "&[data-active]": {
            vars: {
              "--background": theme.colors.primary,
              "--color": theme.colors.onPrimary,
            },
          },
          "&:hover:not([data-active])": {
            vars: {
              "--background": theme.colors.secondary,
              "--color": theme.colors.onSecondary,
            },
          },
        },
      },
      menu: {
        vars: {
          "--background": theme.colors.secondary,
        },
      },
      background: {
        vars: {
          "--background": theme.colors.background,
          "--color": theme.colors.inputBorderFocus,
        },
        selectors: {
          "&[data-active]": {
            color: "var(--onBackground)",
          },
          "&:hover:not([data-active])": {
            vars: {
              "--color": theme.colors.onBackground,
            },
          },
        },
      },
    },
  },
  defaultVariants: {
    variant: "default",
    color: "default",
  },
});

export const tabContentStyle = recipe({
  base: {
    textAlign: "left",
  },
  variants: {
    variant: {
      default: {},
      docs: {},
      outlined: {
        padding: theme.spacings.xs,
        borderRadius: theme.borderRadiuses.xs,
        border: `1px solid ${theme.colors.defaultBorder}`,
        background: theme.colors.background,
        ...responsiveContainer({
          500: {
            padding: theme.spacings.s,
          },
        }),
      },
    },
    indented: {
      true: {
        padding: theme.spacings.m,
      },
    },
  },
});

export const tabStyle = style({
  height: "100%",
  overflow: "hidden",
});
